import React from 'react';
import TextField from '@material-ui/core/TextField';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import MenuItem from '@material-ui/core/MenuItem';
import CircularIndeterminate from 'hcla-web-frontend-primitives/app/components/primitives/Progress/CircularIndeterminate';
import { Typography, WithStyles } from '@material-ui/core';
import createStyles from '@material-ui/core/styles/createStyles';
import withStyles from '@material-ui/core/styles/withStyles';
import Outcome, { OutcomeJSON } from '../../../../../mxGraph/HCLTreeNode/HCLTreeVertexValue/Outcome';
import ReferenceTypes from '../../../../../mxGraph/HCLTreeNode/HCLTreeVertexValue/ReferenceTypes';
import { TreeTypes } from '../Tools/TreeDictionaries';
import TreePickerDialog from '../Dialogs/TreePickerDialog';
import HCLTreeVertex from '../../../../../mxGraph/HCLTreeNode/HCLTreeVertex';
import ProxyTypes from '../../../../../mxGraph/HCLTreeNode/HCLTreeVertexValue/ProxyTypes';
import BayesianNetworkVertex from '../../../../../mxGraph/HCLTreeNode/BayesianNetworkNode/BayesianNetworkVertex';
import BayesianNetworkVertexValue
  from '../../../../../mxGraph/HCLTreeNode/BayesianNetworkNode/BayesianNetworkVertexValue';
import InputRules from './InputRules';
// @ts-ignore
import HCLTreeMxGraphView from '../MxGraphViews/HCLTreeMxGraphView';
import { FaultTreeMxGraphViewJSON } from '../MxGraphViews/FaultTreeMxGraphEditorView';
import { BayesianNetworkMxGraphViewJSON } from '../MxGraphViews/BayesianNetworkMxGraphEditorView';
import HclApiManager from '../../../../../Api/HclApiManager';

const styles = createStyles({
  graphContainer: {
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    height: '40vh',
    overflow: 'auto',
    border: '1px solid #999',
    borderRadius: '12px',
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },

  selectButton: {
    top: '50%',
    transform: 'translateY(-50%)',
    height: '60%',
  },

});

interface Props extends WithStyles<typeof styles> {
  expression: {
    stateRef: Outcome;
    uncertain: boolean;
  };
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement
  | HTMLTextAreaElement>, error?: boolean) => void;
}

interface State {
  isDialogOpen: boolean;
  selectedVertex: BayesianNetworkVertex;
  data: BayesianNetworkMxGraphViewJSON;
  loading: boolean;
  dataValid: boolean;
  stateRefValid: boolean;
  uncertain: boolean;
}

class StateRefInput extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const { expression: { uncertain: uncertainExpr } } = this.props;

    this.state = {
      isDialogOpen: false,
      selectedVertex: null,
      data: null,
      loading: true,
      dataValid: false,
      stateRefValid: true,
      uncertain: uncertainExpr,
    };
  }

  componentDidMount(): void {
    this.fetchData();
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
    if (prevProps.expression.stateRef.getTreeId() === this.props.expression.stateRef.getTreeId() && prevProps.expression.stateRef.getPath() === this.props.expression.stateRef.getPath()) return;
    this.fetchData();
  }

  private toggleDialogOpen = () => {
    this.setState(prevState => ({ isDialogOpen: !prevState.isDialogOpen }));
  };

  private handleSet = (e: React.SyntheticEvent<{}, Event>, selectedVertex: BayesianNetworkVertex) => {
    const { onChange, expression: { stateRef } } = this.props;

    e.persist();
    // @ts-ignore
    e.target.name = 'state_ref';
    // @ts-ignore
    e.target.value = new Outcome(
      stateRef.getName(),
      ReferenceTypes.STATES,
      selectedVertex.getGraph().getModelTreeId(),
      selectedVertex.getKey(),
      ProxyTypes.EVENT_REFERENCE,
    ).toJSON();

    this.setState({ selectedVertex }, () => {
      // @ts-ignore
      onChange(e, !Object.keys((selectedVertex.getValue() as BayesianNetworkVertexValue).getStates()).includes(stateRef.getName()));
    });
  };

  private fetchData = () => {
    const { expression: { stateRef } } = this.props;

    let status: number;
    if (stateRef.getTreeId() !== 0) {
      this.setState({ loading: true }, () => {
        HclApiManager.getHclTreeWithMetadata(stateRef.getTreeId())
          .then((response: any) => {
            status = response.status;
            return response.status && response.json();
          })
          .then((data: BayesianNetworkMxGraphViewJSON) => {
            this.setState({ data, loading: false, dataValid: status === 200 });
          });
      });
    } else {
      this.setState({ loading: false });
    }
  };

  private handleStateNameChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { onChange, expression: { stateRef } } = this.props;
    // @ts-ignore
    e.target.value = { ...stateRef.toJSON(), [e.target.name]: e.target.value };
    e.target.name = 'state_ref';

    onChange(e, false);
  };

  private handleUncertaintyChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { onChange, expression: { uncertain } } = this.props;
    // @ts-ignore
    e.target.value = e.target.checked;
    e.target.name = 'uncertain';

    // @ts-ignore
    this.setState({ uncertain: e.target.checked });
    onChange(e, false);
  };

  render() {
    const { classes, expression: { stateRef } } = this.props;
    const {
      loading, dataValid, isDialogOpen, selectedVertex, data, uncertain: uncertainState,
    } = this.state;

    const stateNames = selectedVertex ? Object.keys((selectedVertex.getValue() as BayesianNetworkVertexValue).getStates()) : [];

    return (
      <React.Fragment>
        <Grid item xs={12}>
          <div className={classes.graphContainer}>
            {
              (loading && <CircularIndeterminate />)
                || (dataValid && <HCLTreeMxGraphView data={data} targetCellKey={stateRef.getPath()} basePath="/model" />)
                || <Typography variant="h5" color="textSecondary">Reference Tree Does not Exist</Typography>
            }
          </div>
        </Grid>
        <Grid container spacing={8} alignItems="center" justify="space-evenly" direction="row">
          <Grid item xs={12} sm={2}>
            <Button
              fullWidth
              variant="outlined"
              size="large"
              onClick={this.toggleDialogOpen}
              color="secondary"
            >
              Select
            </Button>
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              label="Bayesian Network ID"
              name="tree_id"
              variant="outlined"
              value={stateRef.getTreeId()}
              type="number"
              margin="normal"
              fullWidth
              InputLabelProps={{ shrink: true }}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              label="Reference Type"
              name="reference_type"
              variant="outlined"
              value={stateRef.getReferenceType()}
              margin="normal"
              fullWidth
              InputLabelProps={{ shrink: true }}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              label="Node ID"
              name="path"
              value={stateRef.getPath()}
              margin="normal"
              fullWidth
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            {
              selectedVertex
                ? (
                  <TextField
                    label="State"
                    name="name"
                    variant="outlined"
                    value={stateRef.getName()}
                    onChange={this.handleStateNameChange}
                    margin="normal"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    helperText={stateNames.includes(stateRef.getName()) ? undefined : InputRules.DESCRIPTIONS.EMPTY}
                    error={!stateNames.includes(stateRef.getName())}
                    disabled={!selectedVertex}
                    select
                  >
                    {
                      stateNames.map(key => (
                        <MenuItem key={key} value={key}>
                          {key}
                        </MenuItem>
                      ))
                    }
                  </TextField>
                )
                : (
                  <TextField
                    label="State"
                    variant="outlined"
                    name="name"
                    value={stateRef.getName()}
                    margin="normal"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    disabled
                  />
                )
            }
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormControlLabel
              value="top"
              control={(
                <Checkbox
                  onChange={this.handleUncertaintyChange}
                  disabled={!selectedVertex}
                  checked={uncertainState}
                />
              )}
              label="Uncertainty"
              labelPlacement="top"
            />
          </Grid>
        </Grid>
        <TreePickerDialog
          open={isDialogOpen}
          onClose={this.toggleDialogOpen}
          onSet={this.handleSet}
          treeTypes={[TreeTypes.BAYESIAN_NETWORK]}
        />
      </React.Fragment>
    );
  }
}

export default withStyles(styles)(StateRefInput);
